/**
 * Deterministic initials and background color for avatar placeholders.
 * Same user identifier (GUID) always produces the same color.
 */

/** First letter of firstName + first letter of lastName, uppercase. Fallback: first two chars of single name or "?". */
export function getAvatarInitials(
  firstName: string | null | undefined,
  lastName: string | null | undefined
): string {
  const first = (firstName ?? '').trim();
  const last = (lastName ?? '').trim();
  if (first && last) {
    return (first[0] + last[0]).toUpperCase();
  }
  if (first) {
    return first.length >= 2 ? first.slice(0, 2).toUpperCase() : first[0].toUpperCase();
  }
  if (last) {
    return last.length >= 2 ? last.slice(0, 2).toUpperCase() : last[0].toUpperCase();
  }
  return '?';
}

/** Deterministic color from a stable seed (preferably user GUID). */
export function getAvatarInitialsColor(seed: string): string {
  const normalizedSeed = (seed ?? '').trim().toLowerCase();
  if (!normalizedSeed.length) return 'hsl(210 45% 52%)';

  let hash = 0;
  for (let i = 0; i < normalizedSeed.length; i++) {
    hash = (hash << 5) - hash + normalizedSeed.charCodeAt(i);
    hash |= 0;
  }

  // Wider perceived variation while keeping contrast with white initials.
  const hueStep = 23; // spreads nearby seeds farther apart
  const hue = (Math.abs(hash) * hueStep) % 360;
  const saturationPalette = [62, 68, 74, 80];
  const lightnessPalette = [34, 39, 44, 49];
  const saturation = saturationPalette[Math.abs(hash >> 3) % saturationPalette.length];
  const lightness = lightnessPalette[Math.abs(hash >> 7) % lightnessPalette.length];
  return `hsl(${hue} ${saturation}% ${lightness}%)`;
}
